import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getTagsList, getTagsMinimal, deleteTag, setTagsPage, clearCurrentTag } from '../../actions/tagActions';
import ContentManagementSidebar from '../content-management/ContentManagementSidebar';
import TableView from '../content-management/views/TableView';
import MobileView from '../content-management/views/MobileView';
import TableSkeleton from '../content-management/views/TableView/TableSkeleton';
import MobileSkeleton from '../content-management/views/MobileView/MobileSkeleton';
import DeleteConfirmationModal from '../content-management/common/DeleteConfirmationModal';
import PaginationControls from '../common/PaginationControls';
import { Plus, Filter, X } from 'lucide-react';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';
import { loadContentManagementFilters, saveContentManagementFilters } from '../../utils/contentManagementFilters';
import { isAdminRole } from '../../utils/roleHelpers';

const TagsManagementContainer = ({
  auth: { user },
  tag: { tags, minimalTags, loading, currentPage, totalPages, totalTags, hasNextPage, hasPrevPage },
  getTagsList,
  getTagsMinimal,
  deleteTag,
  setTagsPage,
  clearCurrentTag,
  hideSidebar
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState([]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersLoadedRef = useRef(false);

  // Role detection
  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    getTagsMinimal();
  }, [getTagsMinimal]);

  useEffect(() => {
    const saved = loadContentManagementFilters('Tags');
    if (saved?.filters) {
      setSelectedTitles(saved.filters.selectedTitles || []);
      setSelectedCreatedBy(saved.filters.selectedCreatedBy || []);
      if (saved.count > 0) {
        setShowFilters(true);
      }
    }
    filtersLoadedRef.current = true;
    setFiltersHydrated(true);
  }, []);

  useEffect(() => {
    if (!filtersHydrated) return;
    const filters = {};
    if (selectedTitles.length > 0) filters.title = selectedTitles;
    if (selectedCreatedBy.length > 0) filters.createdBy = selectedCreatedBy;
    
    getTagsList(currentPage, 10, filters);
  }, [filtersHydrated, currentPage, selectedTitles, selectedCreatedBy, getTagsList]);

  useEffect(() => {
    if (!filtersLoadedRef.current) return;
    const count = selectedTitles.length + selectedCreatedBy.length;
    saveContentManagementFilters('Tags', { selectedTitles, selectedCreatedBy }, count);
  }, [selectedTitles, selectedCreatedBy]);

  const handleAdd = () => {
    // Clear current tag before navigating to prevent form pre-population
    clearCurrentTag();
    navigate('/content-management/tags/new');
  };

  const handleView = (item) => {
    navigate(`/content-management/tags/${item._id}/view`);
  };

  const handleEdit = (item) => {
    navigate(`/content-management/tags/${item._id}/edit`);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    deleteTag(itemToDelete._id);
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Tags') {
      // Already on tags page, do nothing
      return;
    }
    // Navigate to content management with selected tab
    navigate('/content-management', { state: { activeTab: tabId } });
  };

  const getItemTitle = (item) => {
    return item.title;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

    const getThumbnailUrl = (thumbnail) => {
    if (!thumbnail) return null;
    if (thumbnail.url) return thumbnail.url;
    if (thumbnail.data && thumbnail.contentType) return `data:${thumbnail.contentType};base64,${thumbnail.data}`;
    return null;
  };

  // Handle pagination - only update Redux state, useEffect will fetch data
  const handlePageChange = (newPage) => {
    setTagsPage(newPage);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearAllFilters = () => {
    setSelectedTitles([]);
    setSelectedCreatedBy([]);
    setTagsPage(1);
  };

  const hasActiveFilters = selectedTitles.length > 0 || selectedCreatedBy.length > 0;

  // Get unique values from tags
  const uniqueTitles = minimalTags && minimalTags.length > 0
    ? [...new Set(minimalTags.map(t => t.title))].filter(Boolean)
    : [];
    
  const uniqueCreatedBy = minimalTags && minimalTags.length > 0
    ? [...new Set(minimalTags.map(t => {
        const createdBy = t.createdBy;
        if (!createdBy) return 'Unknown';
        if (typeof createdBy === 'string') return createdBy;
        const firstName = createdBy.name || '';
        const lastName = createdBy.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || 'Unknown';
      }))].filter(Boolean)
    : [];

  const content = (
    <div className={hideSidebar ? '' : "flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto"}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-[#054581]">
              Manage Tags
            </h2>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    hasActiveFilters
                      ? 'bg-[#1086a4] text-white shadow-md'
                      : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                  }`}
                >
                  <Filter size={18} />
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  {hasActiveFilters && (
                    <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                      Active
                    </span>
                  )}
                </button>
                <button
                  onClick={handleAdd}
                  className="bg-[#1086a4] text-white px-2 sm:px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  <span>Add</span>
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-[#054581] hover:underline flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear All
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MultiSelectSearchFilter
                    items={uniqueTitles.map(title => ({ _id: title, title: title }))}
                    selectedItems={selectedTitles}
                    onSelectionChange={(selected) => {
                      setSelectedTitles(selected);
                      setTagsPage(1);
                    }}
                    title="Titles"
                    placeholder="Search titles..."
                    emptyMessage="No titles available"
                  />

                  <MultiSelectSearchFilter
                    items={uniqueCreatedBy.map(creator => ({ _id: creator, title: creator }))}
                    selectedItems={selectedCreatedBy}
                    onSelectionChange={(selected) => {
                      setSelectedCreatedBy(selected);
                      setTagsPage(1);
                    }}
                    title="Creators"
                    placeholder="Search creators..."
                    emptyMessage="No creators available"
                  />
                </div>
              </div>
            )}

            {loading ? (
              <>
                {/* Desktop Skeleton */}
                <div className="hidden md:block">
                  <TableSkeleton rowCount={10} activeTab="Tags" />
                </div>

                {/* Mobile Skeleton */}
                <div className="md:hidden">
                  <MobileSkeleton cardCount={10} />
                </div>
              </>
            ) : (
              <>
                {/* Mobile View */}
                <div className="md:hidden">
                  <MobileView
                    items={tags}
                    activeTab="Tags"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={false}
                  />
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                  <TableView
                    items={tags}
                    activeTab="Tags"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={false}
                  />
                </div>

                {/* Pagination Controls */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalTags}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  itemName="tags"
                />
              </>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
              isOpen={showDeleteConfirmation}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              itemTitle={itemToDelete ? getItemTitle(itemToDelete) : ''}
              itemType="Tag"
            />
      </div>
    </div>
  );

  if (hideSidebar) {
    return <>{content}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Tags"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={false}
        />
        {content}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  tag: state.tag
});

export default connect(
  mapStateToProps,
  {
    getTagsList,
    getTagsMinimal,
    deleteTag,
    setTagsPage,
    clearCurrentTag
  }
)(TagsManagementContainer);
